import {
  extractSalary,
  formatCurrency,
  normalizeDate,
} from "../../../../components/common/Converter";
import { findObjectUseCase, UpsertUseCase } from "../../../../usecases/object";
import { isRetryableError } from "../../../../usecases/object/retryUtils";
import {
  calculateAmount,
  roundDownAmount,
  toCamelCase,
  _chunkArray,
  _omitKeys,
  _calculateAmountRoundDown,
} from "../helper-payroll/HelperFunction";
import {
  _delSpecificFields,
  _buildPayrollData,
  _getContributions,
  _getCsvData,
  _buildCsvLookupMap,
  _getCsvDataKeys,
  _getMatchedFields,
} from "./table-object-helper/FormPresenterHelper";
import { v4 as uuidv4 } from "uuid";
import { payrollCaseRegistry } from "../payroll-cases/PayrollCasesPage";
// import { printComponent } from "nq-component";
// import printComponent from "../../../../printComponent";
import { PrintPayrollTable } from "../print-payroll/PrintPayrollTable";
import BaseFormPresenter from "../../../../base/BaseFormPresenter";

const bulkUpsertUseCase = new UpsertUseCase();

class FormPayrollPresenter extends BaseFormPresenter {
  onChangeObject(object) {
    this.change = object;
    this.object = object;
  }

  get selectedValues() {
    return this.view.props.selectedTypes || {};
  }

  checkCase(caseName) {
    const caseHandler = payrollCaseRegistry[caseName];
    return caseHandler ? caseHandler(this.selectedValues) : false;
  }

  getMatchingCase() {
    for (const caseName in payrollCaseRegistry) {
      if (this.checkCase(caseName)) {
        return caseName;
      }
    }
    return null;
  }

  getCaseListName() {
    const caseMap = {
      isFirstCase: "firstCaseLists",
      isSecondCase: "secondCaseLists",
      isThirdCase: "thirdCaseLists",
      isFourthCase: "fourthCaseLists",
      isFifthCase: "fifthCaseLists",
      isSixthCase: "sixthCaseLists",
      isSeventhCase: "seventhCaseLists",
      isEightCase: "eightCaseLists",
      isTenthCase: "tenthCaseLists",
      isEleventhCase: "eleventhCaseLists",
      isTwelvethCase: "twelveCaseLists",
    };

    const currentCaseKey = Object.keys(caseMap).find((caseKey) =>
      this.checkCase(caseKey)
    );

    return {
      caseLists: caseMap[currentCaseKey],
      cases: currentCaseKey,
    };
  }

  _onPrintPayroll = () => {
    // printComponent(this.view.contractPDF.current);
    const { tableObjects, tableHeaders, tfootObjects, value, signatories } =
      this.view.state;
    const { selectedTypes, otherInfo } = this.view.props;
    const otherData = {
      selectedTypes,
      value,
      signatories,
      otherInfo,
    };
    PrintPayrollTable(tableObjects, tableHeaders, tfootObjects, otherData);
  };

  async fetchSalaryGrade(dateRange, objects, isBackPay = false){
      try {
        if (!objects || objects.length === 0) {
          return {};
        }


        const yearSet = [];
        const ranges = Array.isArray(dateRange) ? dateRange : (dateRange?.startDate && dateRange?.endDate ? [dateRange] : []);
        
        ranges.forEach(range => {
          if (range?.startDate && range?.endDate) {
            const startDate = new Date(range.startDate);
            const endDate = new Date(range.endDate);
            const startYear = startDate.getFullYear();
            const endYear = endDate.getFullYear();
            
            for (let year = startYear; year <= endYear; year++) {
              yearSet.push(year.toString());
            }
          }
        });

        const years = isBackPay ? yearSet : Array.from(new Set(yearSet));

        if (years.length === 0) {
          return {};
        }

        const salaryCodeSet = new Set();
        const objectIdToSalaryCode = {};
        
        for (let i = 0; i < objects.length; i++) {
          const obj = objects[i];
          const code = obj?.salaryCode;
          if (code != null && obj.id) {
            salaryCodeSet.add(code);
            objectIdToSalaryCode[obj.id] = code;
          }
        }
        
        const salaryCodes = Array.from(salaryCodeSet);

        if (salaryCodes.length === 0) {
          return {};
        }

        const codeChunks = _chunkArray(salaryCodes, 100);

        const loadSalaryGradeRows = async (yearList) => {
          const reqs = codeChunks.map((chunk) => {
            const where = {
              salaryCode: { $in: chunk },
              status: "active",
            };
            if (Array.isArray(yearList) && yearList.length > 0) {
              where.Year = { $in: yearList };
            }
            return findObjectUseCase().execute("salary_grade", {
              where,
              keys: ["salaryCode", "Year", "Monthly Rate"],
            });
          });
          return (await Promise.all(reqs)).flat();
        };

        // Latest active row per salaryCode by createdAt (desc)
        const loadLatestSalaryGradeByCode = async (codes) => {
          const chunks = _chunkArray(codes, 100);
          const reqs = chunks.map((chunk) =>
            findObjectUseCase().execute("salary_grade", {
              where: {
                salaryCode: { $in: chunk },
                status: "active",
              },
              sort: { createdAt: -1 },
              keys: ["salaryCode", "Year", "Monthly Rate"],
            })
          );

          const all = (await Promise.all(reqs)).flat();
          const latest = Object.create(null);
          for (let i = 0; i < all.length; i++) {
            const row = all[i];
            const code = row?.salaryCode;
            if (!code) continue;
            if (latest[code] === undefined) latest[code] = row;
          }
          return latest;
        };

        const indexByCodeAndYear = (rows, useExtractedMonthly) => {
          const idx = Object.create(null);
          rows.forEach((sg) => {
            const code = sg?.salaryCode;
            const y = sg?.Year?.toString();
            if (!code || !y) return;
            const val = useExtractedMonthly
              ? extractSalary(sg?.["Monthly Rate"])
              : sg?.["Monthly Rate"];
            if (val == null) return;
            idx[`${code}|${y}`] = val;
          });
          return idx;
        };

        const rateForYearOrLatest = (idx, latestByCode, salaryCode, yearStr) => {
          const direct = idx[`${salaryCode}|${yearStr}`];
          if (direct !== undefined) return direct;
          return latestByCode[salaryCode];
        };

        let rows = await loadSalaryGradeRows(years);
        const latestRowsByCode = Object.create(null);

        if (isBackPay) {
          const byCodeYear = indexByCodeAndYear(rows, true);
          const missingCodes = new Set();
          // More efficient than iterating employees: salaryCodes × yearSet
          for (let c = 0; c < salaryCodes.length; c++) {
            const code = salaryCodes[c];
            for (let j = 0; j < yearSet.length; j++) {
              const y = yearSet[j];
              if (byCodeYear[`${code}|${y}`] === undefined) {
                missingCodes.add(code);
                break;
              }
            }
          }

          if (missingCodes.size > 0) {
            const fetchedLatest = await loadLatestSalaryGradeByCode(
              Array.from(missingCodes)
            );
            Object.assign(latestRowsByCode, fetchedLatest);
          }

          const latestExtractedByCode = Object.create(null);
          Object.keys(latestRowsByCode).forEach((code) => {
            latestExtractedByCode[code] = extractSalary(
              latestRowsByCode[code]?.["Monthly Rate"]
            );
          });

          const out = {};
          const ids = Object.keys(objectIdToSalaryCode);
          for (let i = 0; i < ids.length; i++) {
            const id = ids[i];
            const code = objectIdToSalaryCode[id];
            const rates = [];
            for (let j = 0; j < yearSet.length; j++) {
              const y = yearSet[j];
              const r = rateForYearOrLatest(byCodeYear, latestExtractedByCode, code, y);
              rates.push(r !== undefined ? r : 0);
            }
            out[id] = rates;
          }
          return out;
        }

        const byCodeYearRaw = indexByCodeAndYear(rows, false);
        const yearsNewestFirst = Array.from(
          new Set(yearSet.map((y) => Number(y)))
        ).sort((a, b) => b - a);
        const oneRatePerCode = new Map();

        // Find salary codes that have no rows for any requested year
        const missingCodes = [];
        for (let c = 0; c < salaryCodes.length; c++) {
          const code = salaryCodes[c];
          let hasAny = false;
          for (let yi = 0; yi < yearsNewestFirst.length; yi++) {
            const y = String(yearsNewestFirst[yi]);
            if (byCodeYearRaw[`${code}|${y}`] !== undefined) {
              hasAny = true;
              break;
            }
          }
          if (!hasAny) missingCodes.push(code);
        }

        if (missingCodes.length > 0) {
          const fetchedLatest = await loadLatestSalaryGradeByCode(missingCodes);
          Object.assign(latestRowsByCode, fetchedLatest);
        }

        const latestRawByCode = Object.create(null);
        Object.keys(latestRowsByCode).forEach((code) => {
          latestRawByCode[code] = latestRowsByCode[code]?.["Monthly Rate"];
        });

        for (let c = 0; c < salaryCodes.length; c++) {
          const code = salaryCodes[c];
          for (let yi = 0; yi < yearsNewestFirst.length; yi++) {
            const r = rateForYearOrLatest(
              byCodeYearRaw,
              latestRawByCode,
              code,
              String(yearsNewestFirst[yi])
            );
            if (r !== undefined) {
              oneRatePerCode.set(code, r);
              break;
            }
          }
        }

        const out = {};
        const ids = Object.keys(objectIdToSalaryCode);
        for (let i = 0; i < ids.length; i++) {
          const id = ids[i];
          const code = objectIdToSalaryCode[id];
          const r = oneRatePerCode.get(code);
          if (r !== undefined) out[id] = r;
        }
        return out;
      } catch (error) {
        console.log("error", error)
        throw error;
      }
  }

  _getTableData(data, otherCalculations, requestLists, salaryGrade) {
    const { otherInfo, selectedTypes } = this.view.props;
    const { payItems } = selectedTypes;

    let secondCaseLists = {};
    if (otherCalculations?.secondCaseLists) {
      secondCaseLists = otherCalculations.secondCaseLists;
    }

    const contributions = requestLists?.contributionListMap ?? {};
    const { csv } = otherInfo;

    const isSeventh = this.checkCase("isSeventhCase");
    const getPositionTitle = (obj) => obj.position?.position_title || "N/A";

    const params = {
      checkCase: this.checkCase.bind(this),
      otherCalculations,
      isSeventh,
      getPositionTitle,
    };
    const matchedFields = _getMatchedFields(params);

    if (!matchedFields) return [];

    const csvMap = {};
    (csv || []).forEach((entry) => {
      csvMap[entry.agencyEmployeeNo] = entry;
    });

    const contributionsMap = {};
    (contributions || []).forEach((item) => {
      contributionsMap[item.id] = item;
    });

    const getMatchedCsvRowForObject = (objectId) => {
      if (!otherCalculations || !objectId) return null;
      const caseListKeys = Object.keys(otherCalculations).filter((key) =>
        key.endsWith("CaseLists")
      );

      for (const key of caseListKeys) {
        const caseList = otherCalculations[key];
        const matched = caseList?.[objectId]?.matchedCsvRow;
        if (matched) return matched;
      }

      return null;
    };

    const _buildRow = (obj) => {
      const secondCase = secondCaseLists?.[obj.id] || {};

      const shouldZeroContributions =
        Number(secondCase.earnedBasic || 0) === 0 &&
        Number(secondCase.earnedPeraAda || 0) === 0;

      const row = {};
      for (const key in matchedFields) {
        const value = matchedFields[key];

        if (typeof value === "function") {
          row[key] = value(obj);
        } else if (obj.hasOwnProperty(value)) {
          row[key] =
            value === "salaryGrade" ? extractSalary(salaryGrade[obj.id]) : obj[value];
        } else {
          row[key] = "";
        }
      }
      // Merge CSV data
      const csvEntry = csvMap[obj?.agency?.toString()] || {};

      Object.keys(csvEntry).forEach((key) => {
        if (key !== "agencyEmployeeNo" && key !== "tax") {
          row[key] = csvEntry[key] || "0";
        } else if (key === "tax") {
          const val = csvEntry[key];
          row[key] = val ? Number(val) : 0;
        }
      });

      const matchedCsvRow = getMatchedCsvRowForObject(obj.id);
      if (matchedCsvRow) {
        Object.keys(matchedCsvRow).forEach((key) => {
          if (Object.prototype.hasOwnProperty.call(row, key)) {
            delete row[key];
          }
        });
      }
      // Merge contributions
      const contributionEntry = contributionsMap[obj.id] || {};
      const contributionDetails = contributionEntry.contributions || {};

      Object.keys(contributionDetails).forEach((key) => {
        if (shouldZeroContributions) {
          row[key] = 0;
        }
        if (
          this.checkCase("isThirdCase") &&
          (key === "gsisLR" || key === "pagibig" || key === "phic")
        ) {
          row[key] = [0];
        } else if (payItems === "Last" && key === "pagibig") {
          row[key] = 0;
        }
        // else if (payItems === "Initial" && key === "phic") {
        //   row[key] = 0;
        // }
        else if (this.checkCase("isSecondCase") && key === "gsisLR") {
          const { secondCaseLists } = otherCalculations;
          row[key] = secondCaseLists[obj.id]?.gsis;
        } else if (
          this.checkCase("isSecondCase") &&
          (key === "phic" || key === "pagibig")
        ) {
          const { secondCaseLists } = otherCalculations;
          const dataId = secondCaseLists[obj.id];

          if(dataId.earnedBasic === 0 && dataId.earnedPeraAda === 0) {
            row[key] = 0;
          } else {
            row[key] = dataId[key];
          }

        } else if (this.checkCase("isSeventhCase") && key === "gsisLR") {
          const { seventhCaseLists } = otherCalculations;
          row[key] = seventhCaseLists[obj.id]?.gsis;
        } else {
          row[key] = contributionDetails[key] || "0";
        }
      });
      return row;
    };

    return data.map(_buildRow);
  }

  async getRequests(deductionLists, salaryGrade) {
    console.log("salaryGrade", salaryGrade);
    const { selectedTypes, objects, otherInfo } = this.view.props;
    const contributionArr = deductionLists?.contributions;
    const { contributionType } = deductionLists;

    const groupCases_1 =
      this.checkCase("isFirstCase") ||
      this.checkCase("isSecondCase") ||
      this.checkCase("isThirdCase") ||
      this.checkCase("isFourthCase") ||
      this.checkCase("isSeventhCase") ||
      this.checkCase("isTenthCase");
    const groupCases_2 =
      this.checkCase("isFifthCase") ||
      this.checkCase("isSixthCase") ||
      this.checkCase("isNinthCase");

    const groupCases_3 =
      this.checkCase("isTirteenthCase") || this.checkCase("isFourteenthCase");
    let id = [];
    for (const dataID of objects) {
      id.push(dataID.id);
    }

    let requests = [];
    let taxTable, contributions, allowances, contributionLists, incentives;

    const idChunks = _chunkArray(id, 100);
    if (groupCases_1) {
      requests.push(
        findObjectUseCase().execute("tax-table", {
          keys: ["compensation", "from", "to", "initial", "percent"],
        })
      );
      for (const chunk of idChunks) {
        requests.push(
          findObjectUseCase().execute("contributions", {
            where: {
              empID: { $in: chunk },
            },
          })
        );
        requests.push(
          findObjectUseCase().execute("allowances", {
            where: {
              empID: { $in: chunk },
            },
          })
        );
      }
    } else if (groupCases_2) {
      for (const chunk of idChunks) {
        requests.push(
          findObjectUseCase().execute("allowances", {
            where: { empID: { $in: chunk } },
          })
        );
      }
    } else if (groupCases_3) {
      requests.push(
        findObjectUseCase().execute("incentive", {
          where: {
            id: otherInfo.incentiveId,
          },
        })
      );
    }

    const results = await Promise.all(requests);

    if (groupCases_1) {
      [taxTable, contributions, allowances] = results;
      taxTable = taxTable || [];
      contributions = contributions || [];
      allowances = allowances || [];
    } else if (groupCases_2) {
      [allowances] = results;
      allowances = allowances || [];
    } else if (groupCases_3) {
      [incentives] = results;
      incentives = incentives || [];
    }

    let extractedTaxes = [];
    const contributionListMap = [];

    const allowancesByEmpID = allowances?.reduce((result, record) => {
      const empID = record?.empID;
      if (!result[empID]) {
        result[empID] = [];
      }
      result[empID].push(record);
      return result;
    }, {});

    const allowanceLists = {
      allowance: allowancesByEmpID,
    };

    if (groupCases_1) {
      if (Array.isArray(taxTable)) {
        for (let i = 0; i < taxTable.length; i++) {
          const taxItem = taxTable[i];
          extractedTaxes.push({
            compensation: extractSalary(taxItem?.compensation) || 0,
            from: extractSalary(taxItem?.from) || 0,
            to: extractSalary(taxItem?.to) || 0,
            initial: extractSalary(taxItem?.initial) || 0,
            percent: parseFloat(taxItem?.percent.replace(/[^\d.]/g, "")) || 0,
          });
        }
      }

      const contributionName = new Map();
      if (Array.isArray(contributionArr)) {
        for (const item of contributionArr) {
          const contributionType = toCamelCase(item.contributionType);
          if (!contributionName.has(item.contributionType)) {
            contributionName.set(contributionType, []);
          }
          contributionName.get(contributionType).push({
            roundUp: item.roundUp,
            roundDown: item.roundDown,
          });
        }
      }

      const contributionMap = new Map();
      if (Array.isArray(contributions)) {
        for (const item of contributions) {
          if (!contributionMap.has(item.empID)) {
            contributionMap.set(item.empID, {});
          }
          contributionMap.get(item.empID)[toCamelCase(item.contribution)] =
            item.amount;
        }
      }

      console.log("contributionName", contributionName);

      let mandatoryFormula = {};

      for (const item of objects) {
        
        const monthlyRate = extractSalary(salaryGrade[item.id]);

        console.log("monthlyRate", monthlyRate);

        const contributionsObject = {};

        if (contributionType.length > 0) {
          for (const data of contributionType) {
            const contributionKey = toCamelCase(data);
            const expression = contributionMap.get(item.id)?.[contributionKey];
            mandatoryFormula = contributionMap.get(item.id);

            let value = expression
              ? calculateAmount(expression, monthlyRate)
              : 0;

            if (value !== undefined) {
              const roundingRules = contributionName.get(contributionKey) || [];

              const roundUp = roundingRules.find((rule) => rule.roundUp);
              // const roundDown = roundingRules.find((rule) => rule.roundDown);

              const roundDownRule = roundingRules[0]?.roundDown;

              if (roundUp) {
                value = parseFloat(
                  formatCurrency(value.toString()).replace(/,/g, "") || 0
                );
              } else if (roundDownRule) {
                // value = roundDownAmount(value);
                value = _calculateAmountRoundDown(expression, monthlyRate);
              } else {
                value = parseFloat(
                  formatCurrency(value.toString()).replace(/,/g, "") || 0
                );
              }
            }

            if (contributionKey === "gsisLR" && item?.age >= 65) {
              value = 0;
            }

            contributionsObject[contributionKey] = value;
          }
        }

        contributionListMap.push({
          id: item.id,
          contributions: contributionsObject,
        });
      }


      return {
        extractedTaxes,
        contributionListMap,
        allowanceLists,
        mandatoryFormula,
        contributionName,
      };
    } else if (groupCases_2) {
      return { allowanceLists, contributionListMap };
    } else if (groupCases_3) {
      return { incentives, contributionListMap };
    }

    return { taxTable, allowances, contributionLists, contributionListMap };
  }

  _buildPayrollRecordFromItem(
    item,
    {
      contributions,
      omitKeySet,
      csvMap,
      payrollID,
      caseChecker,
      selectedTypes,
      datePicked,
      joCases,
    }
  ) {
    const allContributions = _getContributions(item, contributions);
    const allCsvData = _getCsvData(item, null, csvMap);
    const formattedCsvArray = allCsvData
      ? Object.entries(allCsvData).map(([key, value]) => `${key}:  ${value}`)
      : [];
    const csvDataKeys = _getCsvDataKeys(allCsvData);
    const omitTheseKeys = [...omitKeySet, ...csvDataKeys];
    const cleanedItem = _omitKeys(item || {}, omitTheseKeys);
    const {
      id,
      biometricId,
      basic,
      salary,
      pera,
      netofMandatory,
      taxDue,
      netPay,
      ...res
    } = cleanedItem;

    _delSpecificFields(res, caseChecker, joCases);

    return _buildPayrollData({
      res,
      cleanedItem,
      allContributions,
      formattedCsvArray,
      payrollID,
      id,
      biometricId,
      netPay,
      pera,
      netofMandatory,
      taxDue,
      basic,
      salary,
      caseChecker,
      selectedTypes,
      datePicked,
      joCases,
    });
  }

  async _onSavePayroll() {
    const { datePicked, selectedTypes, otherInfo } = this.view.props;
    const { jobTypeVal, payItemVal, payrollTypeVal, payItems, jobTypeID } =
      selectedTypes;
    const { tableObjects, requestLists, deductionLists } = this.view.state;
    const { csv, payrollCode, monthFrom, monthTo, periodCovered } = otherInfo;

    const isJoBackPay = jobTypeVal === "Job Order" && payItems === "Last";
    const isJoInitial = jobTypeVal === "Job Order" && payItems === "Initial";

    const contributionsLists = requestLists?.contributionListMap?.[0] ?? [];
    const contributions = contributionsLists?.contributions ?? {};
    const contributionsArray = Object.keys(contributions || {});
    const contributionsName =
      deductionLists?.contributions?.map((c) => c?.contributionType) || [];
    let incentiveName = [];

    const caseChecker = this.checkCase.bind(this);

    if (caseChecker("isTirteenthCase")) {
      const additionalHeader =
        deductionLists?.incentives?.flatMap(
          (item) =>
            item?.otherAdditionals?.map((allowance) => allowance?.name) ?? []
        ) ?? [];
      const deductionsHeader =
        deductionLists?.incentives?.flatMap(
          (item) =>
            item?.otherDeductions?.map((allowance) => allowance?.name) ?? []
        ) ?? [];
      incentiveName.push(...additionalHeader, ...deductionsHeader);
    }

    const omitFields = ["acctNo", "accountNumber", "email", "employeeNumber", "fn", "ln", "mn"];

    const joCases = {
      isJoBackPay,
      isJoInitial,
    };

    const collectionName =
      caseChecker("isThirdCase") ||
      caseChecker("isSixthCase") ||
      caseChecker("isTwelvethCase") ||
      caseChecker("isEightCase") ||
      isJoBackPay
        ? "payroll_back_pay"
        : "payroll";

    const payrollID = uuidv4();

    const payrollList = {
      id: payrollID,
      payrollCode,
      startDate: periodCovered?.startDate || monthFrom,
      endDate: periodCovered?.endDate || monthTo,
      payrollType: payrollTypeVal,
      jobType: jobTypeVal,
      payItem: payItemVal || payItems,
      contributions: contributionsName,
      deductions: deductionLists?.deductionType ?? [],
      incentives: caseChecker("isTirteenthCase") ? incentiveName : [],
      employmentType: { id: jobTypeID },
      stats: "Pending",
    };

    try {
      const totalRecords = tableObjects.length;

      this.view.setState({
        showProgressModal: true,
        saveProgress: 0,
        savedCount: 0,
        totalCount: totalRecords,
      });

      const csvMap = _buildCsvLookupMap(csv);
      const omitKeySet = [...omitFields, ...contributionsArray];

      const buildContext = {
        contributions,
        omitKeySet,
        csvMap,
        payrollID,
        caseChecker,
        selectedTypes,
        datePicked,
        joCases,
      };

      const payrollRecords = tableObjects.map((item) =>
        this._buildPayrollRecordFromItem(item, buildContext)
      );

      await bulkUpsertUseCase.executeMany(collectionName, payrollRecords, {
        progressInterval: 50,
        onProgress: (savedCount) => {
          this.view.setState({
            saveProgress: Math.round((savedCount / totalRecords) * 100),
            savedCount,
          });
        },
      });

      await this.upsertUseCase.execute("payroll_list", payrollList);

      setTimeout(() => {
        this.view.setState({ showProgressModal: false, saveProgress: 0 });
        this.view.props.closeFloating();
        this.view.props.refetchData();
        this.view.showSuccess("Payroll Successfully Saved");
      }, 500);
    } catch (error) {
      console.log(error);
      this.view.setState({ showProgressModal: false, saveProgress: 0 });
      const message = isRetryableError(error)
          ? "Connection overloaded while saving payroll. Please try again — fewer records will save at a time automatically."
          : "Failed to save payroll. Please try again.";
      this.view.showError(message);
    }
  }
}
export default FormPayrollPresenter;